import React, { useCallback, useEffect, useState } from 'react'
import { rem } from 'polished'
import { connect } from 'react-redux'
import styled from 'styled-components'

import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BnblOfferSection from './Components/Shared/BnblOfferSection'
import BnblSwitchPackageDurationStep from './Components/Steps/SwitchPackageDuration/BnblSwitchPackageDurationStep'
import BnblSwitchPackageDurationStepFooter
  from './Components/Steps/SwitchPackageDuration/BnblSwitchPackageDurationStepFooter'
import { BnblSteps, getModalTitle, getOnBack } from './utils'
import { BnblPackageDates } from './Components/Steps/Home/BnblPackageDates'
import { BnblPackageDatesGift } from './Components/Steps/Gift/BnblPackageDatesGift'
import { useAppDispatch } from 'hooks/reduxHooks'
import BnblPackagePricing from './Components/Shared/BnblPackagePricing'
import { Divider } from './Components/Shared/Divider'
import SquareCheckboxInput from 'components/Luxkit/Checkbox/SquareCheckboxInput'
import { updateBnblGifting } from 'actions/BnblModalActions'

const StepContainer = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  padding-left: ${rem(20)};
  padding-right: ${rem(20)};

  ${mediaQueryUp.sm} {
    padding-left: ${rem(24)};
    padding-right: ${rem(40)};
  }
`

interface MappedProps {
  bnblModalPackages: App.BnblModal[];
}

interface Props {
  closeBnblModal: () => void;
  isOpen: boolean;
  offer: App.Offer;
}

function BnblModal(props: Props & MappedProps) {
  const { isOpen, closeBnblModal, offer, bnblModalPackages } = props
  const [step, setStep] = useState<BnblSteps>(BnblSteps.HOME)
  const isGift = bnblModalPackages && bnblModalPackages[0].isGift
  const packageView = bnblModalPackages && bnblModalPackages[0].packageView

  const [selectedPackageUniqueKey, setSelectedPackageUniqueKey] = useState(packageView?.package?.uniqueKey)

  const title = getModalTitle(step)
  const dispatch = useAppDispatch()
  const onBack = getOnBack(step, setStep, () => setSelectedPackageUniqueKey(packageView.package.uniqueKey), isGift)

  const onToggleGifting = useCallback(() => {
    dispatch(updateBnblGifting(offer.id, !isGift))
  }, [dispatch, isGift, offer.id])

  useEffect(() => {
    if (isGift) { setStep(BnblSteps.GIFT) }
  }, [isGift])

  if (!bnblModalPackages) return null
  return (
    <ModalBase
      height="full"
      desktopHeight="auto"
      isOpen={isOpen}
      onClose={closeBnblModal}
    >
      <ModalHeader
        title={title}
        onBackButtonClick={onBack}
        onCloseButtonClick={closeBnblModal}
      />
      {step === BnblSteps.GIFT && (
        <>
          <ModalBody>
            <StepContainer>
              <BnblPackageDatesGift bookByDate={offer.bookByDate} travelByDate={offer.travelToDate} />
              <BnblOfferSection offer={offer} setStep={setStep} />
            </StepContainer>
          </ModalBody>
          <ModalFooter>
            <BnblPackagePricing offer={offer} isGift={isGift} />
          </ModalFooter>
        </>
      )}
      {step === BnblSteps.HOME && (
        <>
          <ModalBody>
            <StepContainer>
              <BnblPackageDates bookByDate={offer.bookByDate} travelByDate={offer.travelToDate} />
              <BnblOfferSection offer={offer} setStep={setStep} />
              <Divider />
              <SquareCheckboxInput onClick={onToggleGifting} checked={isGift} variant="large">
                Gift this package to someone else
              </SquareCheckboxInput>
            </StepContainer>
          </ModalBody>
          <ModalFooter>
            <BnblPackagePricing offer={offer} isGift={isGift} />
          </ModalFooter>
        </>
      )}
      {step === BnblSteps.CHANGE_DURATION && (
        <>
          <ModalBody>
            <StepContainer>
              <BnblSwitchPackageDurationStep
                offer={offer}
                setStep={setStep}
                selectedPackageUniqueKey={selectedPackageUniqueKey}
                setSelectedPackageUniqueKey={setSelectedPackageUniqueKey}
              />
            </StepContainer>
          </ModalBody>
          <BnblSwitchPackageDurationStepFooter offerId={offer.id} selectedPackageUniqueKey={selectedPackageUniqueKey} setStep={setStep}/>
        </>
      )}
    </ModalBase>
  )
}

function mapStateToProps(state: App.State, ownProps: Props): MappedProps {
  return {
    bnblModalPackages: state.offer.bnblModal[ownProps.offer.id],
  }
}

export default connect(mapStateToProps)(BnblModal)
